import { GroupedExperienceOption } from '../ExperiencesStep/RepeatingExperiencesStep/types';
import { ExperienceType, FormStep, PackageType } from '../../types';

export interface ChoiceGroupOption {
  id: number;
  optionId: number;
  name?: string;
  experienceType: ExperienceType;
}

export interface ChoiceGroupDefinition {
  optionGroupId: number | string;
  dayNumber: number;
  dayNumberRange: number[];
  delayAfter?: number;
  selectionsRequired: number;
  options: ChoiceGroupOption[];
}

/**
 * One bookable slot. ecom1 allocates an index per slot in `flattenExperiences`;
 * this is the same idea, so a group needing three options owns three slots
 * rather than one shared entry.
 */
export interface ExperienceSlot {
  index: number;
  groupKey: string;
  optionGroupId: number | string;
  dayNumber: number;
  dayNumberRange: number[];
  delayAfter?: number;
  kind: 'included' | 'choice';
  option?: { id: number; optionId: number; type: ExperienceType };
  choiceGroup?: ChoiceGroupDefinition;
}

export const experienceGroupKey = (dayNumber: number, optionGroupId: number | string) =>
  `${dayNumber}:${optionGroupId}`;

const toDayRange = (dayNumberRange: number[] | undefined, dayNumber: number) =>
  dayNumberRange?.length ? dayNumberRange : [dayNumber];

/**
 * Repeating packages distribute the same group across several days, so the
 * per-day list is the only one that offers every occurrence.
 */
export const getChoiceGroupDefinitions = (
  step: FormStep,
  packageType: PackageType
): ChoiceGroupDefinition[] => {
  const byDayNumber = step.selectExperienceOptionsByDayNumber;
  const sourceDayByGroup = new Map(
    (step.selectExperienceOptions || []).map((group) => [
      group.optionGroupId,
      Number(group.dayNumber ?? 0),
    ])
  );
  const raw =
    packageType === PackageType.REPEATING && byDayNumber?.length
      ? byDayNumber.flatMap(({ dayNumber, options }) =>
          (options || []).map((option) => {
            // distributeRepeatingPackageExperiences overrides dayNumber per
            // occurrence but carries the source day's range, so the range is
            // moved by the same offset to land on this occurrence's nights.
            const occurrenceDay = option.dayNumber ?? dayNumber;
            const sourceDay = sourceDayByGroup.get(option.optionGroupId) ?? occurrenceDay;
            const dayNumberRange = toDayRange(option.dayNumberRange, sourceDay).map(
              (rangeDay) => rangeDay + occurrenceDay - sourceDay
            );
            return { ...option, dayNumber: occurrenceDay, dayNumberRange };
          })
        )
      : step.selectExperienceOptions || [];

  return raw
    .filter((group) => (group.selectionsRequired || 0) > 0 && group.optionGroupId !== undefined)
    .map((group) => {
      const dayNumber = Number(group.dayNumber ?? 0);
      return {
        optionGroupId: group.optionGroupId as number | string,
        dayNumber,
        dayNumberRange: toDayRange(group.dayNumberRange, dayNumber),
        delayAfter: group.delayAfter,
        selectionsRequired: group.selectionsRequired || 1,
        options: (group.options || []) as ChoiceGroupOption[],
      };
    });
};

interface BuildExperienceSlotsParams {
  includedGroups: GroupedExperienceOption[];
  choiceGroups: ChoiceGroupDefinition[];
}

/**
 * Included options are ordered by day, group and option so the slot index a
 * booking sits at does not move as the guest chooses.
 */
export const buildExperienceSlots = ({
  includedGroups,
  choiceGroups,
}: BuildExperienceSlotsParams): ExperienceSlot[] => {
  const choiceKeys = new Set(
    choiceGroups.map(({ dayNumber, optionGroupId }) => experienceGroupKey(dayNumber, optionGroupId))
  );

  const included = includedGroups
    .filter((group) => group.isIncluded)
    .flatMap((group) => {
      const dayNumber = Number(group.dayNumber ?? 0);
      const groupKey = experienceGroupKey(dayNumber, group.optionGroupId);
      if (choiceKeys.has(groupKey)) return [];

      return (group.options || []).map((option) => ({
        groupKey,
        optionGroupId: group.optionGroupId,
        dayNumber,
        dayNumberRange: toDayRange(group.dayNumberRange, dayNumber),
        delayAfter: option.delayAfter,
        kind: 'included' as const,
        option: {
          id: Number(option.id),
          optionId: Number(option.optionId),
          type: option.type,
        },
      }));
    })
    .sort(
      (a, b) =>
        a.dayNumber - b.dayNumber ||
        String(a.optionGroupId).localeCompare(String(b.optionGroupId), undefined, {
          numeric: true,
        }) ||
        (a.option?.optionId ?? 0) - (b.option?.optionId ?? 0)
    );

  const choice = choiceGroups.flatMap((group) =>
    Array.from({ length: group.selectionsRequired }, () => ({
      groupKey: experienceGroupKey(group.dayNumber, group.optionGroupId),
      optionGroupId: group.optionGroupId,
      dayNumber: group.dayNumber,
      dayNumberRange: group.dayNumberRange,
      delayAfter: group.delayAfter,
      kind: 'choice' as const,
      choiceGroup: group,
    }))
  );

  return [...included, ...choice].map((slot, index) => ({ ...slot, index }));
};
